import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Lightbox } from './Lightbox '
import { API_BASE_URL } from 'config/api'
import { ProductImage, getImageUrl, getThumbnailUrl, getImageAlt } from 'utils/imageUtils'

interface ImageSliderProps {
  images: ProductImage[]
  title: string
  autoPlay?: boolean
  autoPlayInterval?: number
}

export const ImageSlider = ({
  images,
  title,
  autoPlay = false,
  autoPlayInterval = 3000,
}: ImageSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLightboxOpen, setLightboxOpen] = useState(false)
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(false)
  const touchStartX = useRef(0)
  const sliderRef = useRef<HTMLDivElement>(null)
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null)

  const hasMultipleImages = images.length > 1
  const thumbnailsPerView = 5
  const maxThumbnailIndex = Math.max(0, images.length - thumbnailsPerView)

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }, [images.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }, [images.length])

  const goToSlide = (slideIndex: number) => {
    setCurrentIndex(slideIndex)
    if (isAutoPlaying) {
      pauseAutoPlay()
    }
  }

  const startAutoPlay = useCallback(() => {
    if (!autoPlay || images.length <= 1 || isLightboxOpen) return

    setIsAutoPlaying(true)
    autoPlayTimerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
    }, autoPlayInterval)
  }, [autoPlay, autoPlayInterval, images.length, isLightboxOpen])

  const pauseAutoPlay = useCallback(() => {
    setIsAutoPlaying(false)
    if (autoPlayTimerRef.current) {
      clearInterval(autoPlayTimerRef.current)
      autoPlayTimerRef.current = null
    }
  }, [])

  const toggleAutoPlay = () => {
    if (isAutoPlaying) {
      pauseAutoPlay()
    } else {
      startAutoPlay()
    }
  }

  useEffect(() => {
    if (autoPlay && !isLightboxOpen) {
      startAutoPlay()
    }
    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current)
      }
    }
  }, [autoPlay, startAutoPlay, isLightboxOpen])

  const goToNextThumbnails = () => {
    setThumbnailStartIndex((prev) => Math.min(prev + 1, maxThumbnailIndex))
  }

  const goToPreviousThumbnails = () => {
    setThumbnailStartIndex((prev) => Math.max(prev - 1, 0))
  }

  useEffect(() => {
    if (currentIndex < thumbnailStartIndex) {
      setThumbnailStartIndex(currentIndex)
    } else if (currentIndex >= thumbnailStartIndex + thumbnailsPerView) {
      setThumbnailStartIndex(
        Math.min(currentIndex - thumbnailsPerView + 1, maxThumbnailIndex),
      )
    }
  }, [currentIndex, thumbnailStartIndex, maxThumbnailIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLightboxOpen) return
      if (e.key === 'ArrowLeft') goToPrevious()
      if (e.key === 'ArrowRight') goToNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToPrevious, goToNext, isLightboxOpen])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === 0) return
    const touchEndX = e.touches[0].clientX
    const touchDiff = touchStartX.current - touchEndX

    if (touchDiff > 50) {
      goToNext()
      touchStartX.current = 0
      if (isAutoPlaying) pauseAutoPlay()
    }
    if (touchDiff < -50) {
      goToPrevious()
      touchStartX.current = 0
      if (isAutoPlaying) pauseAutoPlay()
    }
  }

  const openLightbox = (index: number) => {
    setCurrentIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isLightboxOpen])

  return (
    <div
      className='w-full select-none'
      ref={sliderRef}
      role='region'
      aria-label='گالری تصاویر کتاب'
    >
      <div
        className='relative group rounded-lg overflow-hidden shadow-lg aspect-[3/4]'
        style={{ backgroundColor: 'var(--bg-secondary)' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={() => autoPlay && !isLightboxOpen && startAutoPlay()}
      >
        <div
          className='w-full h-full cursor-pointer'
          onClick={() => openLightbox(currentIndex)}
          onMouseEnter={() => {
            // Preload full image on hover for smooth transition
            if (images[currentIndex]?.image) {
              const link = document.createElement('link')
              link.rel = 'preload'
              link.as = 'image'
              link.href = getImageUrl(images[currentIndex].image)
              document.head.appendChild(link)
            }
          }}
        >
          <img
            src={getImageUrl(images[currentIndex]?.image || '')}
            alt={getImageAlt(images[currentIndex]?.alt, title, currentIndex)}
            title={images[currentIndex]?.title || getImageAlt(images[currentIndex]?.alt, title, currentIndex)}
            className='w-full h-full object-contain p-1 transition-all duration-500 hover:scale-105'
            loading='lazy'
          />
        </div>

        {hasMultipleImages && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToPrevious()
                if (isAutoPlaying) pauseAutoPlay()
              }}
              className='absolute top-1/2 left-2 -translate-y-1/2 z-10 p-2 bg-white/70 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-lg'
              style={{ color: 'var(--text-primary)' }}
              aria-label='تصویر قبلی'
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToNext()
                if (isAutoPlaying) pauseAutoPlay()
              }}
              className='absolute top-1/2 right-2 -translate-y-1/2 z-10 p-2 bg-white/70 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-lg'
              style={{ color: 'var(--text-primary)' }}
              aria-label='تصویر بعدی'
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        <div className='absolute bottom-3 right-3 flex items-center space-x-2 space-x-reverse'>
          <div className='bg-black/60 text-white px-2 py-1 rounded-md text-sm font-medium'>
            {currentIndex + 1} / {images.length}
          </div>
          {autoPlay && hasMultipleImages && (
            <button
              onClick={toggleAutoPlay}
              className='bg-black/60 text-white p-1.5 rounded-md hover:bg-black/80 transition-colors'
              aria-label={isAutoPlaying ? 'توقف پخش خودکار' : 'شروع پخش خودکار'}
            >
              {isAutoPlaying ? (
                <div className='w-3 h-3 bg-white rounded-sm'></div>
              ) : (
                <div className='w-0 h-0 border-l-[6px] border-l-white border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent ml-0.5'></div>
              )}
            </button>
          )}
        </div>
      </div>

      {hasMultipleImages && (
        <div className='mt-4'>
          <div className='relative'>
            <div className='flex justify-center items-center space-x-2 space-x-reverse overflow-hidden'>
              {thumbnailStartIndex > 0 && (
                <button
                  onClick={goToPreviousThumbnails}
                  className='flex-shrink-0 p-1 border rounded-md transition-colors shadow-sm'
                  style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
                  aria-label='نمایش تصاویر قبلی'
                >
                  <ChevronLeft size={16} style={{ color: 'var(--text-secondary)' }} />
                </button>
              )}

              <div className='flex space-x-2 space-x-reverse'>
                {images
                  .slice(
                    thumbnailStartIndex,
                    thumbnailStartIndex + thumbnailsPerView,
                  )
                  .map((img, index) => {
                    const actualIndex = thumbnailStartIndex + index
                    const thumbnailUrl = img.thumbnail 
                      ? getThumbnailUrl(img.thumbnail)
                      : getImageUrl(img.image)
                    return (
                      <button
                        key={actualIndex}
                        onClick={() => goToSlide(actualIndex)}
                        className={`flex-shrink-0 w-12 h-16 md:w-14 md:h-20 aspect-[3/4] rounded-md overflow-hidden transition-all duration-500 border-2 ${
                          currentIndex === actualIndex
                            ? 'border-amber-500 ring-2 ring-amber-200 scale-110 shadow-lg opacity-100 transform rotate-1'
                            : 'opacity-60 hover:opacity-90 hover:scale-105 hover:shadow-md'
                        }`}
                        style={currentIndex !== actualIndex ? { borderColor: 'var(--border-color)' } : {}}
                        aria-label={`رفتن به تصویر ${actualIndex + 1}`}
                      >
                        <img
                          src={thumbnailUrl}
                          alt={getImageAlt(img.alt, title, actualIndex)}
                          title={img.title || getImageAlt(img.alt, title, actualIndex)}
                          className='w-full h-full object-contain transition-transform duration-300'
                          style={{ backgroundColor: 'var(--bg-secondary)' }}
                        />
                      </button>
                    )
                  })}
              </div>

              {thumbnailStartIndex < maxThumbnailIndex && (
                <button
                  onClick={goToNextThumbnails}
                  className='flex-shrink-0 p-1 border rounded-md transition-colors shadow-sm'
                  style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
                  aria-label='نمایش تصاویر بعدی'
                >
                  <ChevronRight size={16} style={{ color: 'var(--text-secondary)' }} />
                </button>
              )}
            </div>

            {images.length > thumbnailsPerView && (
              <div className='flex justify-center mt-2 space-x-1 space-x-reverse'>
                {Array.from({
                  length: Math.ceil(images.length / thumbnailsPerView),
                }).map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                      Math.floor(thumbnailStartIndex / thumbnailsPerView) ===
                      index
                        ? 'bg-amber-500'
                        : ''
                    }`}
                    style={Math.floor(thumbnailStartIndex / thumbnailsPerView) !== index ? { backgroundColor: 'var(--text-tertiary)' } : {}}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isLightboxOpen && (
        <Lightbox
          images={images}
          startIndex={currentIndex}
          onClose={closeLightbox}
          productTitle={title}
          autoPlay
        />
      )}
    </div>
  )
}
